import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { dewarService } from '../services/dewarServiceAppwrite';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { ArrowLeft, Edit, Trash2, Box, Activity } from 'lucide-react';

const DewarDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dewar, setDewar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedRack, setExpandedRack] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isLevelHistoryModalOpen, setIsLevelHistoryModalOpen] = useState(false);
  
  const [dewarFormData, setDewarFormData] = useState({
    name: '',
    location: '',
    temp: '-196°C',
    status: 'Operational',
    liquidNitrogenLevel: 14
  });

  useEffect(() => {
    loadDewarDetails();
  }, [id]);

  const loadDewarDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await dewarService?.getById(id);
      setDewar(data);
    } catch (err) {
      console.error('Error loading dewar details:', err);
      setError(err?.message || 'Failed to load dewar details');
    } finally {
      setLoading(false);
    }
  };

  const handleEditDewar = () => {
    setDewarFormData({
      name: dewar?.name,
      location: dewar?.location || '',
      temp: dewar?.temp,
      status: dewar?.status,
      liquidNitrogenLevel: dewar?.liquidNitrogenLevel || 14
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateDewar = async (e) => {
    e?.preventDefault();
    try {
      setError('');
      await dewarService?.update(id, dewarFormData);
      await loadDewarDetails();
      setIsEditModalOpen(false);
    } catch (err) {
      console.error('Error updating dewar:', err);
      setError(err?.message || 'Failed to update dewar');
    }
  };

  const handleDeleteDewar = async () => {
    if (window?.confirm('Are you sure you want to delete this dewar? All racks and boxes will be removed.')) {
      try {
        setError('');
        await dewarService?.delete(id);
        navigate('/dewars');
      } catch (err) {
        console.error('Error deleting dewar:', err);
        setError(err?.message || 'Failed to delete dewar');
      }
    }
  };

  const handleDeleteBox = async (boxId) => {
    if (window?.confirm('Are you sure you want to delete this box?')) {
      try {
        setError('');
        await dewarService?.deleteBox(boxId);
        await loadDewarDetails();
      } catch (err) {
        console.error('Error deleting box:', err);
        setError(err?.message || 'Failed to delete box');
      }
    }
  };

  const openGridView = (rack, box) => {
    navigate('/box-view-sample-management', {
      state: {
        boxData: {
          dewarId: dewar?.id,
          rackId: rack?.id,
          boxId: box?.id,
          boxName: box?.name,
          dewarName: dewar?.name,
          rackNumber: rack?.number
        }
      }
    });
  };

  const toggleRackExpansion = (rackId) => {
    setExpandedRack(expandedRack === rackId ? null : rackId);
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date?.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDewarInputChange = (e) => {
    const { name, value } = e?.target || {};
    setDewarFormData(prev => ({ ...prev, [name]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dewar details...</p>
        </div>
      </div>
    );
  }

  if (error && !dewar) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Button
              onClick={() => navigate('/dewars')}
              className="bg-gray-200 text-gray-700 hover:bg-gray-300 flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dewars
            </Button>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-800 text-lg font-semibold mb-2">Error Loading Dewar</p>
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!dewar) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Button
              onClick={() => navigate('/dewars')}
              className="bg-gray-200 text-gray-700 hover:bg-gray-300 flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dewars
            </Button>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
            <p className="text-gray-600 text-lg">Dewar not found</p>
          </div>
        </div>
      </div>
    );
  }

  const totalBoxes = dewar?.racks?.reduce((total, rack) => total + (rack?.boxes?.length || 0), 0) || 0;
  const totalSamples = dewar?.racks?.reduce(
    (total, rack) => 
      total + (rack?.boxes?.reduce((boxTotal, box) => boxTotal + (box?.occupied || 0), 0) || 0),
    0
  ) || 0;
  const maxSamples = dewar?.racks?.reduce(
    (total, rack) =>
      total + (rack?.boxes?.reduce((boxTotal, box) => boxTotal + (box?.capacity || 0), 0) || 0),
    0
  ) || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/dewars')}
                className="bg-gray-200 text-gray-700 hover:bg-gray-300 flex items-center"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{dewar?.name}</h1>
                <p className="text-sm text-gray-600">{dewar?.location}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setIsLevelHistoryModalOpen(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white flex items-center"
              >
                <Activity className="w-4 h-4 mr-2" />
                History
              </Button>
              <Button
                onClick={handleEditDewar}
                className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button
                onClick={handleDeleteDewar}
                className="bg-red-600 hover:bg-red-700 text-white flex items-center"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Dewar Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600 mb-1">Temperature</p>
            <p className="text-2xl font-bold text-gray-900">{dewar?.temp}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600 mb-1">Status</p>
            <p className={`text-2xl font-bold ${
              dewar?.status === 'Operational' ? 'text-green-600' :
              dewar?.status === 'Warning' ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {dewar?.status}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600 mb-1">Nitrogen Level</p>
            <div className="flex items-baseline gap-2 mb-3">
              <p className="text-2xl font-bold text-gray-900">{dewar?.liquidNitrogenLevel}"</p>
              <p className="text-sm text-gray-500">/ 22"</p>
            </div>

            {/* Horizontal LN2 Level Gauge */}
            <div className="relative w-full h-3 bg-gray-200 rounded-full overflow-hidden mb-2">
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  (dewar?.liquidNitrogenLevel || 14) <= 4
                    ? 'bg-red-500'
                    : (dewar?.liquidNitrogenLevel || 14) <= 8
                    ? 'bg-yellow-500'
                    : 'bg-green-500'
                }`}
                style={{
                  width: `${Math.min(100, ((dewar?.liquidNitrogenLevel || 14) / 22) * 100)}%`
                }}
              />
            </div>

            <p className={`text-xs font-medium ${
              (dewar?.liquidNitrogenLevel || 14) <= 4 ? 'text-red-600' :
              (dewar?.liquidNitrogenLevel || 14) <= 8 ? 'text-yellow-600' : 'text-green-600'
            }`}>
              {(dewar?.liquidNitrogenLevel || 14) <= 4 ? 'Critical' :
               (dewar?.liquidNitrogenLevel || 14) <= 8 ? 'Low' : 'Good'}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600 mb-1">Samples</p>
            <p className="text-2xl font-bold text-gray-900">
              {totalSamples}/{maxSamples}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {totalBoxes} boxes in {dewar?.racks?.length} racks
            </p>
          </div>
        </div>

        {/* Racks Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Racks & Boxes</h2>
            <p className="text-sm text-gray-600 mt-1">
              {dewar?.racks?.length} racks with {totalBoxes} boxes
            </p>
          </div>
          
          <div className="p-6">
            <div className="space-y-4">
              {dewar?.racks?.map(rack => {
                const rackBoxes = rack?.boxes?.length || 0;
                const rackSamples = rack?.boxes?.reduce((sum, box) => sum + (box?.occupied || 0), 0) || 0;
                const rackMaxSamples = rack?.boxes?.reduce((sum, box) => sum + (box?.capacity || 0), 0) || 0;
                const isExpanded = expandedRack === rack?.id;

                return (
                  <div key={rack?.id} className="border border-gray-200 rounded-lg">
                    <button
                      onClick={() => toggleRackExpansion(rack?.id)}
                      className="w-full p-4 flex justify-between items-center hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Box className="w-5 h-5 text-indigo-600" />
                        <div className="text-left">
                          <p className="font-semibold text-gray-900">{rack?.name}</p>
                          <p className="text-sm text-gray-600">
                            {rackBoxes} boxes • {rackSamples}/{rackMaxSamples} samples
                          </p>
                        </div>
                      </div>
                      <svg
                        className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isExpanded && (
                      <div className="p-4 border-t border-gray-200 bg-gray-50">
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                          {[...(rack?.boxes || [])].sort((a, b) => (a?.name || '').localeCompare(b?.name || '')).map(box => (
                            <div
                              key={box?.id}
                              onClick={() => openGridView(rack, box)}
                              className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer"
                            >
                              <div className="flex justify-between items-start mb-2">
                                <p className="font-semibold text-gray-900">{box?.name}</p>
                                <button
                                  onClick={(e) => {
                                    e?.stopPropagation();
                                    handleDeleteBox(box?.id);
                                  }}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                              <p className="text-xs text-gray-600 mb-1">
                                Type: {box?.type}
                              </p>
                              <p className="text-sm font-medium text-gray-900">
                                {box?.occupied}/{box?.capacity} filled
                              </p>
                              <div className="mt-2 bg-gray-200 rounded-full h-1.5">
                                <div
                                  className="bg-indigo-600 h-1.5 rounded-full"
                                  style={{ width: `${(box?.occupied / box?.capacity) * 100}%` }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </main>
      {/* Edit Dewar Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Edit Dewar</h2>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleUpdateDewar} className="space-y-4">
              <Input
                label="Dewar Name"
                name="name"
                value={dewarFormData?.name}
                onChange={handleDewarInputChange}
                required
                placeholder="Enter dewar name"
              />

              <Input
                label="Location"
                name="location"
                value={dewarFormData?.location}
                onChange={handleDewarInputChange}
                placeholder="e.g., Lab A - Room 101"
              />

              <Input
                label="Temperature"
                name="temp"
                value={dewarFormData?.temp}
                onChange={handleDewarInputChange}
                required
                placeholder="e.g., -196°C"
              />

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Liquid Nitrogen Level (inches)
                </label>
                <Input
                  type="number"
                  name="liquidNitrogenLevel"
                  value={dewarFormData?.liquidNitrogenLevel}
                  onChange={handleDewarInputChange}
                  required
                  min="0"
                  max="22"
                  step="0.5"
                  placeholder="0-22 inches"
                />
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <input
                      type="range"
                      name="liquidNitrogenLevel"
                      value={dewarFormData?.liquidNitrogenLevel}
                      onChange={handleDewarInputChange}
                      min="0"
                      max="22"
                      step="0.5"
                      className="w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-indigo-600 [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-indigo-600 [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer"
                    />
                    <div className="absolute top-0 left-0 w-full flex justify-between text-xs text-gray-500 mt-3 pointer-events-none">
                      <span>0"</span>
                      <span>22"</span>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-gray-700 min-w-[60px]">
                    {dewarFormData?.liquidNitrogenLevel}"
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Status</label>
                <select
                  name="status"
                  value={dewarFormData?.status}
                  onChange={handleDewarInputChange}
                  className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
                >
                  <option value="Operational">Operational</option>
                  <option value="Warning">Warning</option>
                  <option value="Maintenance">Maintenance</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="flex-1 bg-gray-200 text-gray-700 hover:bg-gray-300"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
                >
                  Update Dewar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Liquid Nitrogen Level History Modal */}
      {isLevelHistoryModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    Liquid Nitrogen Level History
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {dewar?.name} - Current Level: {dewar?.liquidNitrogenLevel}"
                  </p>
                </div>
                <button
                  onClick={() => setIsLevelHistoryModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {dewar?.levelHistory && dewar?.levelHistory?.length > 0 ? (
                <div className="space-y-3">
                  {[...dewar?.levelHistory]?.reverse()?.map((entry) => {
                    const isIncrease = entry?.previousLevel !== null && entry?.level > entry?.previousLevel;
                    const isDecrease = entry?.previousLevel !== null && entry?.level < entry?.previousLevel;
                    
                    return (
                      <div 
                        key={entry?.id} 
                        className={`p-4 rounded-lg border-l-4 ${
                          isIncrease 
                            ? 'bg-green-50 border-green-500' 
                            : isDecrease 
                            ? 'bg-yellow-50 border-yellow-500' : 'bg-blue-50 border-blue-500'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`px-2 py-1 text-xs font-semibold rounded ${
                                isIncrease 
                                  ? 'bg-green-100 text-green-800' 
                                  : isDecrease 
                                  ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                              }`}>
                                {entry?.action}
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatDate(entry?.date)}
                              </span>
                            </div>
                            <p className="text-sm font-semibold text-gray-900">
                              {entry?.previousLevel !== null ? (
                                <>
                                  {entry?.previousLevel}" → {entry?.level}"
                                  <span className={`ml-2 text-xs ${
                                    isIncrease ? 'text-green-600' : 'text-yellow-600'
                                  }`}>
                                    ({isIncrease ? '+' : ''}{(entry?.level - entry?.previousLevel)?.toFixed(1)}")
                                  </span>
                                </>
                              ) : (
                                `Initial Level: ${entry?.level}"`
                              )}
                            </p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mt-2">
                          <div>
                            <span className="font-semibold">Checked By:</span> {entry?.checkedBy}
                          </div>
                          <div>
                            <span className="font-semibold">Status:</span>{' '}
                            <span className={`font-medium ${
                              entry?.level <= 4 
                                ? 'text-red-600' 
                                : entry?.level <= 8 
                                ? 'text-yellow-600' : 'text-green-600'
                            }`}>
                              {entry?.level <= 4 
                                ? 'Critical' 
                                : entry?.level <= 8 
                                ? 'Low' : 'Good'
                              }
                            </span>
                          </div>
                        </div>
                        
                        {entry?.notes && (
                          <div className="mt-2 text-xs text-gray-700 bg-white p-2 rounded">
                            <span className="font-semibold">Notes:</span> {entry?.notes}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">No level history available for this dewar.</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <Button
                onClick={() => setIsLevelHistoryModalOpen(false)}
                className="w-full bg-gray-600 text-white hover:bg-gray-700"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DewarDetails;